import styles from "./info-pages.module.css";

interface TermsPageProps {}

export const TermsPage = (props: TermsPageProps) => {
  return (
    <div className={styles.container}>
      <div className={styles.innerContainer}>
        <div className={styles.header}>
          <h1>Terms of service</h1>
        </div>
        <LastUpdated />
        <About />
        <AccountRegistration />
        <UseOfService />
        <Payments />
        <IntellectualProperty />
        <Availability />
        <Termination />
        <Liability />
        <ProductQuality />
        <GoverningLaw />
        <Contact />
      </div>
    </div>
  );
};

const About = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>1) About us</h2>
      </div>
      <div className={styles.sectionContent}>
        <p>
          <b>Trailart.store</b> is a UK-based platform that allows users to
          generate stylised map images of their runs, hikes, and other
          activities. These are available as high-quality printed wall art
          delivered to your door.
        </p>
      </div>
    </div>
  );
};

const LastUpdated = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>Last updated 13th December 2024</h2>
      </div>
      <div className={styles.sectionContent}>
        <p>
          Welcome to <b>Trailart.store</b>. These Terms of Service ("Terms")
          govern your access to and use of our website and services. By using
          our site, you agree to be bound by these Terms.
        </p>
      </div>
    </div>
  );
};

const AccountRegistration = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>2) Account registration</h2>
      </div>
      <div className={styles.sectionContent}>
        <p>
          To use certain features, you must create an account by providing a
          valid email address and password. You are responsible for maintaining
          the confidentiality of your login credentials and for all activities
          under your account.
        </p>
      </div>
    </div>
  );
};

const UseOfService = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>3) Use of our service</h2>
      </div>
      <div className={styles.sectionContent}>
        <div className={styles.sectionContent}>
          <h3>You agree not to:</h3>
          <ul className={styles.unorderedList}>
            <li>Use our services for any unlawful purpose</li>
            <li>
              Attempt to reverse-engineer or interfere with the functionality of
              the site
            </li>
            <li>Submit content or data you do not have the rights to use</li>
            <li>
              Use automated scripts to interact with the service without
              permission
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const Payments = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>4) Payments and Physical Products</h2>
      </div>
      <div className={styles.sectionContent}>
        <p>
          <strong>Payment:</strong> All purchases are processed securely via
          Stripe. Payment is taken in full before your order is sent for
          printing. We do not store your payment information.
        </p>
        <p>
          <strong>Customization:</strong> Your products are custom-made to your
          specifications using your GPS data and design choices. Each item is
          unique to you.
        </p>
        <p>
          <strong>No Cooling-Off Period:</strong> Under the UK Consumer
          Contracts Regulations 2013, customized products made to your
          specifications are exempt from the standard 14-day cancellation
          right. Once you submit your order, it cannot be cancelled as it is
          sent immediately to our printing partner for production.
        </p>
        <p>
          <strong>Delivery:</strong> Your order will be delivered within 4-7
          working days to addresses in the UK only.
        </p>
        <p>
          <strong>Quality Guarantee:</strong> All products are printed by our
          reputable printing partner using high-quality materials with
          excellent color accuracy and print quality.
        </p>
        <p>
          <strong>Returns and Refunds:</strong> We only offer refunds or
          replacements for items that arrive damaged, are faulty, or are not as
          described. If you experience any of these issues:
        </p>
        <ul className={styles.unorderedList}>
          <li>
            Contact us at{" "}
            <a href="mailto:support@trailart.store">support@trailart.store</a>{" "}
            within 30 days of receipt
          </li>
          <li>We will arrange a replacement or provide a full refund</li>
          <li>Return shipping costs will be covered by TrailArt</li>
        </ul>
        <p>
          <strong>Your Consumer Rights:</strong> Your statutory rights under
          the Consumer Rights Act 2015 are not affected by these terms.
        </p>
      </div>
    </div>
  );
};

const IntellectualProperty = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>5) Intellectual Property</h2>
      </div>
      <div className={styles.sectionContent}>
        <p>
          <strong>Our Content:</strong> All designs, styles, maps, and site
          content are the intellectual property of TrailArt.store and may not
          be reproduced or redistributed without permission.
        </p>
        <p>
          <strong>Your Content:</strong> You retain full ownership of any GPS
          data, images, and other materials you upload. We do not claim any
          rights to your content beyond the non-exclusive licence to use it to
          generate your custom map output. You may request deletion of your
          data at any time.
        </p>
        <p>
          <strong>Your Responsibility:</strong> You warrant that you have the
          necessary rights and permissions for any content you upload
          (including GPS data, images, and other materials). You are solely
          responsible for ensuring you do not infringe any third-party
          intellectual property rights. TrailArt will not be liable for any
          claims arising from your uploaded content.
        </p>
      </div>
    </div>
  );
};

const Availability = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>6) Availability</h2>
      </div>
      <div className={styles.sectionContent}>
        <div className={styles.sectionContent}>
          <p>
            We strive to ensure uninterrupted service but cannot guarantee 100%
            uptime. We may suspend access temporarily for maintenance or updates
            without prior notice.
          </p>
        </div>
      </div>
    </div>
  );
};

const Termination = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>7) Termination</h2>
      </div>
      <div className={styles.sectionContent}>
        <p>
          We reserve the right to suspend or terminate your access to the site
          if you violate these Terms or use the service in a harmful or
          unlawful way.
        </p>
      </div>
    </div>
  );
};

const Liability = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>8) Limitation of Liability</h2>
      </div>
      <div className={styles.sectionContent}>
        <p>
          Our service is provided "as is" without any warranties, express or
          implied. To the fullest extent permitted by law, TrailArt's liability
          is limited to the purchase price of your order.
        </p>
        <p>
          We are not liable for any indirect, consequential, or incidental
          damages arising from your use of our service or products.
        </p>
        <p>
          <strong>Important:</strong> Nothing in these Terms limits our
          liability for death or personal injury caused by our negligence,
          fraud or fraudulent misrepresentation, or any other liability that
          cannot be excluded or limited under UK law. Your statutory consumer
          rights are not affected.
        </p>
      </div>
    </div>
  );
};

const ProductQuality = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>9) Product Quality</h2>
      </div>
      <div className={styles.sectionContent}>
        <p>
          All products are printed using high-quality materials by our
          reputable printing partner. We take great care to ensure excellent
          color accuracy and print quality.
        </p>
        <p>
          <strong>Please note:</strong> Colors may vary slightly from how they
          appear on your screen due to differences in display settings and
          calibration.
        </p>
        <p>
          If your product arrives damaged, is faulty, or is not as described,
          please contact us at{" "}
          <a href="mailto:support@trailart.store">support@trailart.store</a>{" "}
          within 30 days of receipt. We will arrange a replacement or full
          refund.
        </p>
        <p>
          We cannot be held liable for print quality issues that arise from
          low-quality or low-resolution images you have uploaded. Please ensure
          your uploaded images are of sufficient quality for printing.
        </p>
      </div>
    </div>
  );
};

const GoverningLaw = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>10) Governing Law</h2>
      </div>
      <div className={styles.sectionContent}>
        <p>
          These Terms are governed by the laws of the United Kingdom. Any
          disputes will be subject to the exclusive jurisdiction of the courts
          of the United Kingdom.
        </p>
      </div>
    </div>
  );
};

const Contact = () => {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>11) Contact</h2>
      </div>
      <div className={styles.sectionContent}>
        <p>
          If you have any questions about these Terms, contact us at{" "}
          <a href="mailto:support@trailart.store">support@trailart.store</a>
        </p>
      </div>
    </div>
  );
};
